import React from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faStar} from '@fortawesome/free-solid-svg-icons'
import {useAppSelector} from '../../store/hooks'
import {selectSearchParams} from '../../store/slices/searchParamsSlice'
import {useSearchServiceOffersQuery} from '../../services/ibeApi'
import {FocusTrap} from 'focus-trap-react'
import ShoppingCartPricingSummary from '../cart/ShoppingCartPricingSummary'
import NoShowPolicy from '../common/NoShowPolicy'
import CancellationPolicy from '../common/CancellationPolicy'
import {booking} from '@whyhotel/common-schema'
import {formatCurrency} from '../../services/utils'
import {isMonthlyOffer} from '../../services/offerUtils'
import {selectUnitGroupsById} from '../../store/slices/configSlice'

export interface SearchUnitOfferDetailsModalProps {
  offer: booking.BookingOffer
  isOpen: boolean
  onClose: () => void
}

const SearchUnitOfferDetailsModal = ({
  offer,
  isOpen,
  onClose
}: SearchUnitOfferDetailsModalProps) => {
  const ratePlanId = offer?.ratePlan?.id || ''
  const ratePlanDescription = offer?.ratePlan?.description || ''
  const searchParams = useAppSelector(selectSearchParams)
  const isMonthly = isMonthlyOffer(offer)
  const serviceOffersQuery = useSearchServiceOffersQuery(
    {
      ratePlanId,
      guests: searchParams.guests || 1,
      dateRange: searchParams.dateRange
    },
    {skip: !offer || !ratePlanId || isMonthly}
  )
  const serviceOffers = serviceOffersQuery.data?.services || []
  const serviceOffersLoading = serviceOffersQuery.isLoading
  const unitGroupConfigsById = useAppSelector(selectUnitGroupsById)
  const unitGroupConfig = unitGroupConfigsById[offer?.unitGroup?.id]
  const unitGroupName = unitGroupConfig?.name || offer?.unitGroup?.name

  return (
    <>
      {isOpen && (
        <FocusTrap active={isOpen}>
          <div
            className="modal modal-open"
            onClick={e => {
              if (e.target === e.currentTarget) {
                onClose()
              }
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-box w-[95%] lg:max-w-screen-md max-h-[95%]">
              <div className="modal-header">
                <button
                  className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                  onClick={onClose}
                  aria-label="Close unit offer details modal."
                >
                  ✕
                </button>
                <h2 className="font-semibold text-xl">{unitGroupName}</h2>
                <h3 className="text-base">{offer.ratePlan.name}</h3>
              </div>
              <div className="border-t mt-1"></div>

              <div className="modal-body mt-4 overflow-y-auto max-h-[500px]">
                <div className="flex flex-col gap-4">
                  {/* Rate Description */}
                  {ratePlanDescription?.length && (
                    <div className="rounded-lg bg-gray-100 p-3">
                      <h3 className="font-semibold border-b mb-2">
                        Rate Details
                      </h3>
                      <p
                        className="text-sm"
                        dangerouslySetInnerHTML={{__html: ratePlanDescription}}
                      />
                    </div>
                  )}

                  {/* Member Rate Details */}
                  {offer.isMemberRate && (
                    <div className="rounded-lg bg-gray-100 p-3">
                      <h3 className="font-semibold border-b mb-2">
                        Placemakr Insider Rate Details
                      </h3>
                      <p className="text-sm">
                        Book direct as a Placemakr Insider and receive a
                        discounted rate.
                      </p>
                      <div className="flex flex-row items-center text-sm gap-2 mt-2">
                        <FontAwesomeIcon
                          icon={faStar}
                          className="text-accent mr-1"
                          title="Placemakr Insider Rate"
                        />
                        <p className="italic">
                          It's always free and easy to join as a Placemakr
                          Insider. Complete sign up at checkout.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Cancellation Policy */}
                  <div className="rounded-lg bg-gray-100 p-3">
                    <CancellationPolicy offer={offer} />
                  </div>

                  {/* No Show Policy */}
                  <div className="rounded-lg bg-gray-100 p-3">
                    <NoShowPolicy offer={offer} />
                  </div>

                  {/* Pricing */}
                  <div className="rounded-lg bg-gray-100 p-3">
                    <h3 className="font-semibold border-b mb-3">
                      Pricing Breakdown
                    </h3>
                    <ShoppingCartPricingSummary
                      offer={offer}
                      additionalServices={[]}
                      isCart={false}
                    />
                  </div>

                  {/* Extended Stay Requirements */}
                  {isMonthly && (
                    <div className="rounded-lg bg-gray-100 p-3">
                      <h3 className="font-semibold border-b mb-3">
                        Extended Stay Requirements
                      </h3>
                      <p className="text-sm">
                        For reservations of 28 nights or longer, Placemakr
                        requires guest verification and a soft credit check to
                        confirm eligibility for extended-stay bookings. These
                        steps do not affect a guest’s credit score and are used
                        solely in connection with long-term stays.
                      </p>
                      <p className="text-sm mt-3">
                        Failure to complete guest verification or the required
                        soft credit check may result in cancellation of the
                        reservation. In such cases, any payments made are
                        non-refundable
                      </p>
                    </div>
                  )}

                  {/* Service Details */}
                  {!isMonthly &&
                    !serviceOffersLoading &&
                    serviceOffers &&
                    serviceOffers.length > 0 && (
                      <div className="rounded-lg bg-gray-100 p-3">
                        <h3 className="font-semibold border-b mb-2">
                          Optional Services
                        </h3>
                        <>
                          {serviceOffers.map(
                            (serviceOffer: booking.ServiceOffer) => {
                              return (
                                <div
                                  key={serviceOffer.service.id}
                                  className="flex flex-row justify-between w-full text-sm"
                                >
                                  <div>{serviceOffer.service.name}</div>
                                  <div>
                                    {formatCurrency(
                                      serviceOffer.totalAmount.grossAmount
                                    )}{' '}
                                    per{' '}
                                    <span className="lowercase">
                                      {serviceOffer.service.pricingUnit}
                                    </span>
                                  </div>
                                </div>
                              )
                            }
                          )}
                        </>
                      </div>
                    )}

                  {/* Placemakr Policies */}
                  <div className="rounded-lg bg-gray-100 p-3 flex flex-col gap-3">
                    <h3 className="font-semibold border-b mb-2">
                      Placemakr Policies
                    </h3>

                    <p className="text-sm">
                      <span className="font-medium">
                        Reservation Guarantee:
                      </span>{' '}
                      All reservations require a valid credit card at the time
                      of booking. Your reservation is held until midnight on the
                      day of arrival unless prior arrangements are made. After
                      midnight, no-show policies apply.
                    </p>

                    <p className="text-sm">
                      <span className="font-medium">Payment Policy:</span> If
                      payment was not made at the time of booking, your credit
                      card will be charged one day before check-in for all
                      bookings made on{' '}
                      <a
                        href="https://www.placemakr.com"
                        className="link link-accent"
                        target="_blank"
                      >
                        Placemakr.com
                      </a>
                      .
                    </p>

                    <p className="text-sm">
                      <span className="font-medium">Incidental Hold:</span> On
                      the morning of arrival, a $150 incidental hold will be
                      placed on your card and released within 72 hours of
                      checkout. At Placemakr Buzzard Point, guests under the age
                      of 25 will have a $300 hold placed on their form of
                      payment (guests 25+ will have our standard $150 hold).
                    </p>

                    <p className="text-sm">
                      <span className="font-medium">ID Verification:</span> All
                      reservations require that the guest present a valid
                      government-issued ID prior to check-in.
                    </p>

                    <p className="text-sm">
                      <span className="font-medium">Terms and Conditions:</span>{' '}
                      View our full{' '}
                      <a
                        href="https://www.placemakr.com/terms-and-conditions"
                        className="link link-accent"
                        target="_blank"
                      >
                        Terms and Conditions
                      </a>
                      .
                    </p>
                  </div>

                  {/* Contact Info */}
                  <div className="italic text-sm text-center text-primary my-2">
                    Have questions? Contact us at hello@placemakr.com
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </>
  )
}

export default SearchUnitOfferDetailsModal
